import isMobileJs from 'ismobilejs';
import { detect } from 'detect-browser';
import PCancelable from 'p-cancelable';
import { Viewport } from './events/Viewport';
import { PageLoad } from './events/PageLoad';
import version from '../version';
/**
 * Vevet Application
 */
export class Application {
    /**
     * @example
     * const app = Application({
     *     page: 'home'
     * });
     */
    constructor(data = {}) {
        /**
         * Pages (instances)
         */
        this._pages = [];
        /**
         * Current Page (instance).
         */
        this._page = false;
        // check if the application already exists
        if (window.vevetApp) {
            throw new Error('Vevet Application already exists!');
        }
        // set defaults
        this._prop = Object.assign(Object.assign({}, this.defaultProp), data);
        this._prefix = this.prop.prefix;
        // initialize the application
        // Define that you're using Vevet
        this._sayHi();
        // add the application to the window
        window.vevetApp = this;
        // get type of device
        const ifmobile = isMobileJs();
        this._isPhone = ifmobile.phone;
        this.html.classList.toggle(`${this.prefix}phone`, this._isPhone);
        this._isTablet = ifmobile.tablet;
        this.html.classList.toggle(`${this.prefix}tablet`, this._isTablet);
        this._isMobile = ifmobile.phone || ifmobile.tablet;
        this.html.classList.toggle(`${this.prefix}mobile`, this._isMobile);
        this._isDesktop = !this._isMobile;
        this.html.classList.toggle(`${this.prefix}desktop`, this._isDesktop);
        // get browser info
        const browserData = detect();
        // get OS name
        if (browserData === null || browserData === void 0 ? void 0 : browserData.os) {
            const osName = browserData.os.split(' ')[0].toLowerCase();
            this.html.classList.add(`${this.prefix}os-${osName}`);
            this._osName = osName;
        }
        else {
            this._osName = '';
        }
        // get browser name
        if (browserData === null || browserData === void 0 ? void 0 : browserData.name) {
            const browserName = browserData.name.toLowerCase();
            this.html.classList.add(`${this.prefix}browser-${browserName}`);
            this._browserName = browserName;
        }
        else {
            this._browserName = '';
        }
        // browserData
        // create default helpers
        this._pageLoad = new PageLoad();
        this._viewport = new Viewport();
        // check webp support
        if (this.prop.webpSupport) {
            const testWebP = new Image();
            testWebP.onload = () => {
                this._supportsWebp = testWebP.width === 1;
            };
            testWebP.onerror = () => {
                this._supportsWebp = false;
            };
            testWebP.src = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
        }
        else {
            this._supportsWebp = false;
        }
    }
    get version() {
        return version;
    }
    /**
     * Application properties.
     */
    get prop() {
        return this._prop;
    }
    /**
     * Default properties.
     */
    get defaultProp() {
        return {
            sayHi: true,
            tablet: 1199,
            phone: 899,
            prefix: 'v-',
            easing: [0.25, 0.1, 0.25, 1],
            viewportResizeTimeout: 0,
            webpSupport: false,
        };
    }
    get prefix() {
        return this._prefix;
    }
    get isPhone() {
        return this._isPhone;
    }
    get isTablet() {
        return this._isTablet;
    }
    get isMobile() {
        return this._isMobile;
    }
    get isDesktop() {
        return this._isDesktop;
    }
    get osName() {
        return this._osName;
    }
    get browserName() {
        return this._browserName;
    }
    get supportsWebp() {
        return this._supportsWebp;
    }
    /**
     * Get document element.
     */
    get doc() {
        return document;
    }
    /**
     * Get HTML element
     */
    get html() {
        return document.documentElement;
    }
    /**
     * Get body element.
     */
    get body() {
        return document.body;
    }
    /**
     * Get an array of existing pages.
     * A new element is added to the array when {@linkcode Vevet.Page.create} is called.
     */
    get pages() {
        return this._pages;
    }
    /**
     * Get the current page instance.
     */
    get page() {
        return this._page;
    }
    /**
     * Set the current page instance.
     */
    set page(val) {
        this._page = val;
    }
    /**
     * Action on page created
     */
    onPageCreated() {
        return new PCancelable((resolve) => {
            if (this._page) {
                this._page.onCreate().then(() => {
                    resolve(this.page);
                });
                return;
            }
            setTimeout(() => {
                this.onPageCreated().then(() => {
                    resolve(this.page);
                });
            }, 30);
        });
    }
    /**
     * Action on page shown
     */
    onPageShown() {
        return new PCancelable((resolve) => {
            this.onPageCreated().then(() => {
                if (this._page) {
                    this._page.onShow().then(() => {
                        resolve(this.page);
                    });
                }
            });
        });
    }
    get pageLoad() {
        return this._pageLoad;
    }
    /**
     * Action on page laoded
     */
    onPageLoaded() {
        return new PCancelable((resolve) => {
            this._pageLoad.onLoaded(() => {
                resolve();
            });
        });
    }
    get viewport() {
        return this._viewport;
    }
    /**
     * Defines that you're using Vevet.
     */
    _sayHi() {
        if (!this.prop.sayHi) {
            return;
        }
        const msg = `Vevet ${this.version}`;
        const style = [
            'padding: 1rem 1.5rem;',
            'background: #5F2580;',
            'font: 1rem/1 Arial;',
            'color: #ffffff;',
        ].join('');
        // eslint-disable-next-line no-console
        console.log('%c%s', style, msg);
    }
}
